import { useParams, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Scale, MapPin, ArrowRight, CheckCircle2, Award, Phone, MessageCircle, Languages, IdCard, Landmark, Gavel, Route, Compass, Wifi } from "lucide-react";
import Seo from "@/components/Seo";
import LegalDisclaimerBanner from "@/components/LegalDisclaimerBanner";
import { breadcrumbJsonLd, cityLegalServiceJsonLd, BASE_KEYWORDS } from "@/lib/seo";
import { CITY_CONTENT, CITY_PRACTICE_TAGS } from "@/lib/lawyerContent";
import { OnlineBadge } from "@/components/OnlineBadge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import api from "@/lib/api";
import imageUrl from "@/lib/imageUrl";

export const CITIES = [
  { slug: "delhi", name: "Delhi" },
  { slug: "mumbai", name: "Mumbai" },
  { slug: "chennai", name: "Chennai" },
  { slug: "kanpur", name: "Kanpur" },
  { slug: "bangalore", name: "Bangalore" },
  { slug: "hyderabad", name: "Hyderabad" },
  { slug: "pune", name: "Pune" },
  { slug: "ahmedabad", name: "Ahmedabad" },
  { slug: "gurugram", name: "Gurugram" },
  { slug: "chandigarh", name: "Chandigarh" },
];

const waLink = (num) => `https://wa.me/${(num || "").replace(/[^0-9]/g, "")}`;

const faqsFor = (city) => [
  { q: `How do I find a Supreme Court lawyer in ${city}?`, a: `Litigation India connects people in ${city} with experienced Supreme Court of India advocates who volunteer free, pro bono initial guidance. Request a free callback and we will match you with a suitable advocate for your matter.` },
  { q: `Is the initial legal guidance free for ${city} residents?`, a: `Yes. Initial guidance from our volunteer advocate network is free (pro bono) and educational, for litigants and junior lawyers in ${city} and across India.` },
  { q: `Can a Supreme Court lawyer from ${city} help with bail matters?`, a: `Yes. Our advocates guide on bail matters, appeals, Special Leave Petitions (SLPs), and criminal, civil, constitutional and service matters connected to the Supreme Court of India.` },
  { q: `Do I need to travel to Delhi for a Supreme Court case from ${city}?`, a: `Not for initial guidance. You can get help remotely (phone/WhatsApp) from ${city}. Supreme Court hearings are in New Delhi, and our advocates can explain the process and pathways.` },
  { q: `How much does a Supreme Court lawyer in ${city} cost?`, a: `Litigation India's initial guidance is free. If you later need formal representation, our advocates can explain likely pathways and connect you with practising Supreme Court and High Court advocates.` },
  { q: `Which High Court hears appeals from ${city} before a matter reaches the Supreme Court?`, a: `Appeals and writs from ${city} are heard by the ${CITY_CONTENT[slugOf(city)]?.highCourt || "State High Court"}. A Special Leave Petition against that court's judgment must ordinarily be filed in the Supreme Court within 90 days.` },
];

const slugOf = (name) => (CITIES.find((c) => c.name === name) || {}).slug || "";

function CityLawyerCard({ lw }) {
  return (
    <div className="flex flex-col gap-4 rounded-2xl border bg-card p-5 sm:flex-row" data-testid={`city-lawyer-card-${lw.id}`}>
      <Avatar className="h-16 w-16 shrink-0">
        <AvatarImage src={imageUrl(lw.photo, { w: 160 })} alt={lw.name} width={64} height={64} loading="lazy" decoding="async" className="object-cover" />
        <AvatarFallback>{lw.name?.[0]}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <h3 className="font-serif text-lg font-bold">{lw.name}</h3>
        <p className="text-sm font-medium text-primary">{lw.specialization_en}</p>
        {lw.online_available && <div className="mt-1"><OnlineBadge /></div>}
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
          {lw.experience_years ? <span className="flex items-center gap-1"><Award className="h-3.5 w-3.5" /> {lw.experience_years}+ yrs</span> : null}
          {lw.location ? <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {lw.location}</span> : null}
          {lw.languages?.length ? <span className="flex items-center gap-1"><Languages className="h-3.5 w-3.5" /> {lw.languages.join(", ")}</span> : null}
          {lw.bar_council_id ? <span className="flex items-center gap-1"><IdCard className="h-3.5 w-3.5" /> Enrolment {lw.bar_council_id}</span> : null}
        </div>
        {lw.helps_en?.length ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {lw.helps_en.slice(0, 4).map((h, i) => <span key={i} className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-secondary-foreground">{h}</span>)}
          </div>
        ) : null}
        {lw.bio_en ? <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{lw.bio_en}</p> : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Button asChild size="sm" data-testid={`city-lawyer-profile-${lw.id}`}>
          <Link to={`/supreme-court-lawyers/${lw.id}`}>View Profile <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
        </Button>
        {lw.phone && (
          <Button asChild size="sm" variant="outline" aria-label="Call">
            <a href={`tel:${lw.phone.replace(/\s/g, "")}`}><Phone className="h-3.5 w-3.5" /></a>
          </Button>
        )}
        {(lw.whatsapp || lw.phone) && (
          <Button asChild size="sm" variant="outline" aria-label="WhatsApp">
            <a href={waLink(lw.whatsapp || lw.phone)} target="_blank" rel="noreferrer"><MessageCircle className="h-3.5 w-3.5" /></a>
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CityLawyer() {
  const { city } = useParams();
  const cfg = CITIES.find((c) => c.slug === (city || "").toLowerCase());

  // React rules of hooks: ALL hooks must be called before any conditional return.
  const { data: cityLawyers = [], isLoading: lawyersLoading } = useQuery({
    queryKey: ["lawyers-city", city],
    queryFn: async () => (await api.get(`/public/lawyers?city_slug=${city}`)).data,
    enabled: !!cfg,
  });

  if (!cfg) return <Navigate to="/supreme-court-lawyers" replace />;

  const name = cfg.name;
  const path = `/supreme-court-lawyer/${cfg.slug}`;
  const onlineLawyers = cityLawyers.filter((lw) => lw.online_available);
  const faqs = faqsFor(name);
  const content = CITY_CONTENT[cfg.slug];

  const seo = {
    title: `Our Top Supreme Court Lawyers/Advocates in ${name} — Legal Services & Free Guidance | Litigation India`,
    description: `Supreme Court lawyers in ${name}: experienced advocates for appeals, SLPs, bail, service and property matters. Free pro bono initial guidance for ${name} residents.`.slice(0, 158),
    keywords: `Supreme Court lawyers in ${name}, Supreme Court advocate ${name}, legal services in ${name}, top advocates in ${name}, ${name} lawyer for Supreme Court, bail lawyer ${name}, ${BASE_KEYWORDS}`,
    path,
  };

  const jsonLd = [
    cityLegalServiceJsonLd(name, path, cityLawyers),
    { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "Supreme Court Lawyers", path: "/supreme-court-lawyers" },
      { name: `${name}`, path },
    ]),
  ];

  return (
    <div>
      <Seo {...seo} jsonLd={jsonLd} />

      <section className="li-hero-gradient border-b">
        <div className="mx-auto w-full max-w-4xl px-4 py-16 sm:px-6">
          <Badge className="gap-1 bg-primary/10 text-primary hover:bg-primary/10"><MapPin className="h-3.5 w-3.5" /> {name}</Badge>
          <h1 className="mt-4 font-serif text-4xl font-bold tracking-tight sm:text-5xl">Our Top Supreme Court Lawyers/Advocates in {name}</h1>
          <p className="mt-4 text-muted-foreground reading-prose">
            Searching for <strong>Supreme Court lawyers in {name}</strong>? Litigation India connects litigants and junior lawyers in {name} with experienced Supreme Court of India advocates — for appeals, Special Leave Petitions, bail, service, property and family matters — offering free, pro bono initial guidance. Appeals from {name} are heard by the {content?.highCourt}, and from there by the Supreme Court in New Delhi.
          </p>
          <Button asChild className="mt-6 grad-cta text-white" data-testid="city-cta"><Link to={`/free-legal-help?city=${encodeURIComponent(name)}`}>Request a Free Callback <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        </div>
      </section>

      {/* Legal Disclaimer Banner */}
      <LegalDisclaimerBanner />

      {/* ── ONLINE LAWYERS FIRST — shown at the very top of main content ── */}
      <section className="mx-auto w-full max-w-4xl px-4 pt-10 sm:px-6">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 dark:border-emerald-800 dark:bg-emerald-950/20" data-testid="online-assistance-section">
          <h2 className="flex items-center gap-2 font-serif text-2xl font-bold">
            <Wifi className="h-6 w-6 text-emerald-600" /> Lawyers Available for Online Guidance in {name}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground reading-prose">
            The following Supreme Court advocates serving {name} offer online guidance — by phone, WhatsApp or video call — so you can get a free initial consultation without travelling to Delhi. Online guidance is educational and does not replace formal engagement of an advocate.
          </p>
          {lawyersLoading ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
            </div>
          ) : onlineLawyers.length > 0 ? (
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {onlineLawyers.map((lw) => (
                <li key={lw.id} className="flex items-center gap-3 rounded-xl border bg-card p-3" data-testid={`online-lawyer-${lw.id}`}>
                  <Avatar className="h-12 w-12 shrink-0">
                    <AvatarImage src={imageUrl(lw.photo, { w: 128 })} alt={lw.name} width={48} height={48} className="object-cover" />
                    <AvatarFallback>{lw.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <Link to={`/supreme-court-lawyers/${lw.id}`} className="font-semibold hover:text-primary">{lw.name}</Link>
                    <p className="truncate text-xs text-muted-foreground">{lw.specialization_en}</p>
                    <div className="mt-1"><OnlineBadge /></div>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    {lw.phone && (
                      <Button asChild size="sm" variant="outline" aria-label="Call">
                        <a href={`tel:${lw.phone.replace(/\s/g, "")}`}><Phone className="h-3.5 w-3.5" /></a>
                      </Button>
                    )}
                    {(lw.whatsapp || lw.phone) && (
                      <Button asChild size="sm" variant="outline" aria-label="WhatsApp">
                        <a href={waLink(lw.whatsapp || lw.phone)} target="_blank" rel="noreferrer"><MessageCircle className="h-3.5 w-3.5" /></a>
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-4 rounded-xl border border-dashed bg-card p-4 text-sm text-muted-foreground" data-testid="online-assistance-empty">
              Online-guidance availability for {name} is being updated. You can still <Link to="/free-legal-help" className="font-medium text-primary underline-offset-2 hover:underline">request a callback</Link> and a Supreme Court advocate will connect with you remotely.
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto w-full max-w-4xl space-y-10 px-4 py-10 sm:px-6">

        {/* All lawyers section — every advocate listed for this city */}
        <div>
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-serif text-2xl font-bold"><Scale className="h-6 w-6 text-primary" /> All Supreme Court Advocates Serving {name}</h2>
            <Button asChild variant="outline" size="sm">
              <Link to="/supreme-court-lawyers">View all lawyers</Link>
            </Button>
          </div>
          <div className="mt-4 space-y-4" data-testid="city-lawyers-list">
            {lawyersLoading ? (
              Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)
            ) : cityLawyers.length > 0 ? (
              cityLawyers.map((lw) => <CityLawyerCard key={lw.id} lw={lw} />)
            ) : (
              <div className="rounded-2xl border border-dashed p-8 text-center">
                <p className="text-muted-foreground text-sm">No lawyers currently listed for {name}.</p>
                <p className="mt-1 text-muted-foreground text-xs">Browse all our Supreme Court advocates below.</p>
                <Button asChild variant="outline" size="sm" className="mt-4">
                  <Link to="/supreme-court-lawyers">View all Supreme Court lawyers <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
                </Button>
              </div>
            )}
          </div>
          {cityLawyers.length > 0 && (
            <div className="mt-4">
              <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                <Link to="/supreme-court-lawyers">Browse all Supreme Court lawyers <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
              </Button>
            </div>
          )}
        </div>

        {content && (
          <article className="space-y-8" data-testid="city-long-form">
            <div>
              <h2 className="flex items-center gap-2 font-serif text-2xl font-bold"><Landmark className="h-6 w-6 text-primary" /> The legal landscape in {name}</h2>
              <p className="mt-3 reading-prose text-muted-foreground">{content.landscape}</p>
            </div>
            <div>
              <h2 className="flex items-center gap-2 font-serif text-2xl font-bold"><Gavel className="h-6 w-6 text-primary" /> Cases commonly taken from {name} to the Supreme Court</h2>
              <p className="mt-3 reading-prose text-muted-foreground">{content.cases}</p>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {CITY_PRACTICE_TAGS.map((x) => (
                  <li key={x} className="flex items-start gap-2 text-sm text-muted-foreground"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Top advocates in {name} for {x.toLowerCase()}</li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="flex items-center gap-2 font-serif text-2xl font-bold"><Route className="h-6 w-6 text-primary" /> How {name} lawyers connect with the Supreme Court</h2>
              <p className="mt-3 reading-prose text-muted-foreground">{content.connect}</p>
            </div>
            <div>
              <h2 className="flex items-center gap-2 font-serif text-2xl font-bold"><Compass className="h-6 w-6 text-primary" /> Practical guidance for {name} residents seeking legal help</h2>
              <p className="mt-3 reading-prose text-muted-foreground">{content.guidance}</p>
            </div>
          </article>
        )}

        <div>
          <h2 className="font-serif text-2xl font-bold">Frequently asked questions — Supreme Court lawyer in {name}</h2>
          <Accordion type="single" collapsible className="mt-4">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`} data-testid="city-faq-item">
                <AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="reading-prose text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6">
          <h2 className="font-serif text-xl font-bold">Talk to a Supreme Court advocate from {name}</h2>
          <p className="mt-2 text-muted-foreground">Free, pro bono initial guidance. No obligation.</p>
          <Button asChild className="mt-4 grad-cta text-white"><Link to={`/free-legal-help?city=${encodeURIComponent(name)}`}>Request Free Callback</Link></Button>
        </div>
      </section>
    </div>
  );
}
